import { useQuery } from '@tanstack/react-query';
import { QUERY_KEYS } from '../Constants/queryKeys';
import {
  GetInternshipProcessesByOrganizations,
  GetInternshipProcessesByUniversityDepartment,
  GetInternshipProcessesForRector,
  GetInternshipProcessesOfTutor,
  GetInternshipProcessesOfUniversitySupervisor,
} from '../helper/apiHelper';
import { useConventionSignContext } from '../context/ConventionSignContext';
import { Roles } from '../Types/interfaces';
import { useHasRole } from './useHasRole';

export const useInternshipProcessesByRole = (
  userId: number,
  role: Roles,
  orgIds?: number[]
) => {
  const hasRole = useHasRole();
  const isOrganizationRepresentative = hasRole(
    Roles.OrganizationRepresentative
  );
  const isTutor = hasRole(Roles.Tutor);
  const isCadruDidactic = hasRole(Roles.CadruDidactic);
  const isRector = hasRole(Roles.Rector);

  return useQuery({
    queryKey: [
      ...QUERY_KEYS.INTERNSHIP_PROCESSES_BY_ROLE,
      role,
      orgIds,
      userId,
      isTutor,
      isCadruDidactic,
      isRector,
      isOrganizationRepresentative,
    ],
    queryFn: async () => {
      switch (role) {
        case Roles.OrganizationRepresentative:
          if (!orgIds || orgIds.length === 0) return [];
          return isOrganizationRepresentative
            ? await GetInternshipProcessesByOrganizations(orgIds)
            : [];
        case Roles.Tutor:
          return isTutor ? await GetInternshipProcessesOfTutor(userId) : [];
        case Roles.CadruDidactic:
          return isCadruDidactic
            ? await GetInternshipProcessesOfUniversitySupervisor(userId)
            : [];
        case Roles.Rector:
          return isRector ? await GetInternshipProcessesForRector(userId) : [];
        default:
          return [];
      }
    },
    enabled: !!role && !!userId,
  });
};

export const useInternshipProcessesByOrganizations = (
  organizationIds: number[],
  isReadyToFetch: boolean
) => {
  const { entitySignings } = useConventionSignContext();
  return useQuery({
    queryKey: [
      ...QUERY_KEYS.INTERNSHIP_PROCESSES_BY_ORGANIZATIONS,
      organizationIds,
      entitySignings,
    ],
    queryFn: async () => {
      return await GetInternshipProcessesByOrganizations(organizationIds);
    },
    enabled: isReadyToFetch && !!organizationIds && organizationIds.length > 0,
  });
};

export const useInternshipProcessesOfTutor = (
  tutorId: number,
  isReadyToFetch: boolean
) => {
  const { entitySignings } = useConventionSignContext();
  return useQuery({
    queryKey: [
      ...QUERY_KEYS.INTERNSHIP_PROCESSES_OF_TUTOR,
      tutorId,
      entitySignings,
    ],
    queryFn: async () => {
      return await GetInternshipProcessesOfTutor(tutorId);
    },
    enabled: isReadyToFetch && !!tutorId,
  });
};

export const useInternshipProcessesOfUniversitySupervisor = (
  supervisorId: number,
  isReadyToFetch: boolean = true
) => {
  const { entitySignings } = useConventionSignContext();
  return useQuery({
    queryKey: [
      ...QUERY_KEYS.INTERNSHIP_PROCESSES_OF_SUPERVISOR,
      supervisorId,
      entitySignings,
    ],
    queryFn: async () => {
      return await GetInternshipProcessesOfUniversitySupervisor(supervisorId);
    },
    enabled: isReadyToFetch && !!supervisorId,
  });
};

export const useInternshipProcessesOfRector = (
  rectorId: number,
  isReadyToFetch: boolean
) => {
  const { entitySignings } = useConventionSignContext();
  const hasRole = useHasRole();
  return useQuery({
    queryKey: [
      ...QUERY_KEYS.INTENRSHIP_PROCESSES_OF_RECTOR,
      rectorId,
      entitySignings,
    ],
    queryFn: () => GetInternshipProcessesForRector(rectorId),
    enabled: isReadyToFetch && !!rectorId && hasRole(Roles.Rector),
  });
};

export const useInternshipProcessesOfUniversityDepartment = (
  departmentId: number
) => {
  return useQuery({
    queryKey: [...QUERY_KEYS.PROCESSES_BY_UNIVERSITY_DEPARTMENT, departmentId],
    queryFn: async () => {
      return await GetInternshipProcessesByUniversityDepartment(
        String(departmentId)
      );
    },
    enabled: !!departmentId,
  });
};
